import { useCallback } from 'react';
import { useUIHelperContext } from '../context/UIHelperContext';
import { isValidRef } from '../utils/refResolver';

/**
 * Hook for managing UI helpers (onboarding, tooltips, etc.)
 * Provides methods to show, hide, and manage helper overlays
 *
 * @returns {Object} Helper methods
 * @returns {Function} showHelper - Show a helper overlay
 * @returns {Function} hideHelper - Hide a specific helper or current helper
 * @returns {Function} hideAllHelpers - Hide all active helpers
 */
export default function useUIHelper() {
  const { showHelper: contextShowHelper, hideHelper, hideAllHelpers } = useUIHelperContext();

  const showHelper = useCallback((config) => {
    // Validate required fields
    if (!config.ref) {
      console.error('UIHelper: ref is required');
      return;
    }

    if (!config.content) {
      console.error('UIHelper: content is required');
      return;
    }

    // Validate ref can be resolved to DOM element
    if (!isValidRef(config.ref)) {
      console.error('UIHelper: ref cannot be resolved to a DOM element. Make sure the ref points to a DOM element or a component that forwards refs properly.');
      return;
    }

    // Default values
    const helperConfig = {
      id: config.id || `helper-${Date.now()}`,
      ref: config.ref,
      position: config.position || 'bottom',
      content: config.content,
      offset: config.offset || 8,
      arrow: config.arrow !== false, // Default to true
      backdrop: config.backdrop || 'blur',
      fontSize: config.fontSize || 14, // Default font size in px
      ...config,
    };

    contextShowHelper(helperConfig);
  }, [contextShowHelper]);

  return {
    showHelper,
    hideHelper,
    hideAllHelpers,
  };
}
